import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import {
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  BeakerIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import { format, parseISO, startOfDay, endOfDay, subDays } from "date-fns";
import { nl } from "date-fns/locale";
import { exportToCsv } from "../utils/exportCsv";
import Pagination from "../components/Pagination";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

interface AuditLogEntry {
  id: string;
  pharmacy_id: string;
  user_id: string;
  user_name: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  entity_label: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

const ENTITY_ICONS: Record<string, React.FC<{ className?: string }>> = {
  task: ClipboardDocumentListIcon,
  complaint: ExclamationTriangleIcon,
  calibration: ScaleIcon,
  preparation: BeakerIcon,
};

const ENTITY_LABELS: Record<string, string> = {
  task: "Taak",
  complaint: "Klacht",
  calibration: "Kalibratie",
  preparation: "Bereiding",
};

const ACTION_LABELS: Record<string, string> = {
  completed: "Voltooid",
  cancelled: "Geannuleerd",
  resolved: "Opgelost",
  closed: "Gesloten",
  calibrated: "Geijkt",
  approved: "Goedgekeurd",
  rejected: "Afgewezen",
  validated: "Gevalideerd",
};

const ACTION_COLORS: Record<string, string> = {
  completed: "text-green-700 bg-green-50 border-green-200",
  calibrated: "text-green-700 bg-green-50 border-green-200",
  approved: "text-green-700 bg-green-50 border-green-200",
  validated: "text-green-700 bg-green-50 border-green-200",
  resolved: "text-blue-700 bg-blue-50 border-blue-200",
  closed: "text-gray-700 bg-gray-50 border-gray-200",
  cancelled: "text-red-700 bg-red-50 border-red-200",
  rejected: "text-red-700 bg-red-50 border-red-200",
};

const PAGE_SIZE = 25;

const QUICK_RANGES = [
  { label: "Vandaag", days: 0 },
  { label: "7 dagen", days: 7 },
  { label: "30 dagen", days: 30 },
  { label: "90 dagen", days: 90 },
];

const USER_COLORS = ["#6366f1", "#22d3ee", "#f59e0b", "#22c55e", "#f97316", "#ec4899", "#8b5cf6", "#14b8a6"];

const AuditLog: React.FC = () => {
  const { selectedPharmacy } = useAuth();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState<"log" | "activity">("log");
  const [activityEntries, setActivityEntries] = useState<AuditLogEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const today = format(new Date(), "yyyy-MM-dd");
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(today);
  const [entityTypeFilter, setEntityTypeFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (selectedPharmacy) fetchEntries();
  }, [selectedPharmacy, page, dateFrom, dateTo, entityTypeFilter, actionFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, entityTypeFilter, actionFilter, search]);

  // Fetch all entries (no pagination) for the activity tab
  useEffect(() => {
    if (selectedPharmacy && activeTab === "activity") fetchActivityData();
  }, [selectedPharmacy, activeTab, dateFrom, dateTo]);

  const fetchEntries = async () => {
    if (!selectedPharmacy) return;
    setLoading(true);
    try {
      let query = supabase
        .from("audit_logs")
        .select("*", { count: "exact" })
        .eq("pharmacy_id", selectedPharmacy.id)
        .gte("created_at", startOfDay(parseISO(dateFrom)).toISOString())
        .lte("created_at", endOfDay(parseISO(dateTo)).toISOString())
        .order("created_at", { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (entityTypeFilter !== "all") query = query.eq("entity_type", entityTypeFilter);
      if (actionFilter !== "all") query = query.eq("action", actionFilter);

      const { data, count, error } = await query;
      if (error) throw error;
      setEntries(data ?? []);
      setTotal(count ?? 0);
    } catch (err) {
      console.error("Error fetching audit logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityData = async () => {
    if (!selectedPharmacy) return;
    setActivityLoading(true);
    try {
      const { data, error } = await supabase
        .from("audit_logs")
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .gte("created_at", startOfDay(parseISO(dateFrom)).toISOString())
        .lte("created_at", endOfDay(parseISO(dateTo)).toISOString())
        .order("created_at", { ascending: false })
        .limit(2000);
      if (error) throw error;
      setActivityEntries(data ?? []);
    } catch (err) {
      console.error("Error fetching activity data:", err);
    } finally {
      setActivityLoading(false);
    }
  };

  const applyQuickRange = (days: number) => {
    setDateTo(today);
    setDateFrom(days === 0 ? today : format(subDays(new Date(), days), "yyyy-MM-dd"));
  };

  const filtered = search.trim()
    ? entries.filter(
        (e) =>
          e.user_name.toLowerCase().includes(search.toLowerCase()) ||
          (e.entity_label ?? "").toLowerCase().includes(search.toLowerCase()),
      )
    : entries;

  const handleExport = () => {
    const headers = ["Datum", "Gebruiker", "Actie", "Type", "Item", "Details"];
    const rows = filtered.map((e) => [
      format(parseISO(e.created_at), "dd-MM-yyyy HH:mm"),
      e.user_name,
      ACTION_LABELS[e.action] ?? e.action,
      ENTITY_LABELS[e.entity_type] ?? e.entity_type,
      e.entity_label ?? "",
      e.details ? JSON.stringify(e.details) : "",
    ]);
    exportToCsv(`auditlog_${dateFrom}_${dateTo}.csv`, headers, rows);
  };

  // Per-user activity stats for the activity tab
  const userStats = useMemo(() => {
    const map: Record<string, { user_name: string; total: number; byType: Record<string, number>; byAction: Record<string, number>; lastActive: string }> = {};
    activityEntries.forEach((e) => {
      if (!map[e.user_id]) {
        map[e.user_id] = { user_name: e.user_name, total: 0, byType: {}, byAction: {}, lastActive: e.created_at };
      }
      const u = map[e.user_id];
      u.total++;
      u.byType[e.entity_type] = (u.byType[e.entity_type] || 0) + 1;
      u.byAction[e.action] = (u.byAction[e.action] || 0) + 1;
      if (e.created_at > u.lastActive) u.lastActive = e.created_at;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [activityEntries]);

  const topAction = (byAction: Record<string, number>) => {
    const entries = Object.entries(byAction);
    if (entries.length === 0) return "—";
    const best = entries.sort((a, b) => b[1] - a[1])[0];
    return ACTION_LABELS[best[0]] ?? best[0];
  };

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Auditlog</h1>
            <p className="text-gray-600 mt-1">
              Overzicht van voltooide acties per gebruiker
            </p>
          </div>
          {activeTab === "log" && (
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="-ml-1 mr-2 h-4 w-4" />
              CSV
            </button>
          )}
        </div>
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("log")}
              className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center space-x-2 ${
                activeTab === "log"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <ListBulletIcon className="h-4 w-4" />
              <span>Log</span>
            </button>
            <button
              onClick={() => setActiveTab("activity")}
              className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center space-x-2 ${
                activeTab === "activity"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <UserGroupIcon className="h-4 w-4" />
              <span>Gebruikersactiviteit</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Date range filters — always visible for both tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 space-y-4">
        {/* Quick ranges */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500 mr-1">Snel:</span>
          {QUICK_RANGES.map((r) => (
            <button
              key={r.label}
              onClick={() => applyQuickRange(r.days)}
              className="px-3 py-1 text-xs font-medium rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          {/* Date from */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Van</label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={(e) => setDateFrom(e.target.value)}
              className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          {/* Date to */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tot</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              max={today}
              onChange={(e) => setDateTo(e.target.value)}
              className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          {/* Entity type — log tab only */}
          {activeTab === "log" && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
            <select
              value={entityTypeFilter}
              onChange={(e) => setEntityTypeFilter(e.target.value)}
              className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Alle types</option>
              <option value="task">Taken</option>
              <option value="complaint">Klachten</option>
              <option value="calibration">Kalibraties</option>
              <option value="preparation">Bereidingen</option>
            </select>
          </div>
          )}
          {/* Action — log tab only */}
          {activeTab === "log" && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Actie</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Alle acties</option>
              <option value="completed">Voltooid</option>
              <option value="cancelled">Geannuleerd</option>
              <option value="resolved">Opgelost</option>
              <option value="closed">Gesloten</option>
              <option value="calibrated">Geijkt</option>
              <option value="approved">Goedgekeurd</option>
              <option value="validated">Gevalideerd</option>
            </select>
          </div>
          )}
          {/* Search — log tab only */}
          {activeTab === "log" && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Zoeken</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Gebruiker of item…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          )}
        </div>
      </div>

      {activeTab === "activity" && (
        <div className="space-y-6">
          {activityLoading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Laden…</div>
          ) : userStats.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500 text-sm">
              Geen activiteit gevonden in de geselecteerde periode.
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Totale acties</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{activityEntries.length}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Actieve gebruikers</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{userStats.length}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Meest actief</p>
                  <p className="text-lg font-bold text-gray-900 mt-1 truncate">{userStats[0]?.user_name ?? "—"}</p>
                  <p className="text-xs text-gray-500">{userStats[0]?.total ?? 0} acties</p>
                </div>
              </div>

              {/* Bar chart: actions per user */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Acties per gebruiker</h2>
                <ResponsiveContainer width="100%" height={Math.max(180, userStats.length * 44)}>
                  <BarChart
                    data={userStats.map((u) => ({ name: u.user_name, acties: u.total }))}
                    layout="vertical"
                    margin={{ top: 4, right: 24, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => [v ?? 0, "acties"]} />
                    <Bar dataKey="acties" radius={[0, 4, 4, 0]}>
                      {userStats.map((_, i) => (
                        <Cell key={i} fill={USER_COLORS[i % USER_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Per-user breakdown table */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gebruiker</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Totaal</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Taken</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Klachten</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Kalibraties</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Bereidingen</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meest voorkomend</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Laatste actie</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {userStats.map((u, i) => (
                      <tr key={u.user_name} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: USER_COLORS[i % USER_COLORS.length] }}>
                              {u.user_name.charAt(0).toUpperCase()}
                            </span>
                            {u.user_name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{u.total}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">{u.byType.task ?? 0}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">{u.byType.complaint ?? 0}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">{u.byType.calibration ?? 0}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">{u.byType.preparation ?? 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{topAction(u.byAction)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {format(parseISO(u.lastActive), "d MMM yyyy HH:mm", { locale: nl })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "log" && (
      <>
      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Laden…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            Geen auditlog vermeldingen gevonden voor de geselecteerde filters.
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum / Tijd
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gebruiker
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actie
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map((entry) => {
                  const Icon =
                    ENTITY_ICONS[entry.entity_type] ?? ClipboardDocumentListIcon;
                  return (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {format(parseISO(entry.created_at), "dd MMM yyyy", { locale: nl })}
                        <span className="block text-xs text-gray-400">
                          {format(parseISO(entry.created_at), "HH:mm")}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {entry.user_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            ACTION_COLORS[entry.action] ??
                            "text-gray-700 bg-gray-50 border-gray-200"
                          }`}
                        >
                          {ACTION_LABELS[entry.action] ?? entry.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-1.5">
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          {ENTITY_LABELS[entry.entity_type] ?? entry.entity_type}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                        {entry.entity_label ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* Summary */}
      {!loading && (
        <p className="mt-3 text-xs text-gray-400 text-right">
          {total} {total === 1 ? "vermelding" : "vermeldingen"} gevonden
        </p>
      )}
      </>
      )}
    </div>
  );
};

export default AuditLog;
